import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SecurityService } from '@app/_core/services/security.service';
import { ConfigService } from '@app/config.services';
import { DuplicateUserComponent } from '@app/pages/login/duplicate-user/duplicate-user.component';
import { ErrorPageComponent } from '@app/pages/login/error-page/error-page.component';
import * as CryptoJS from 'crypto-js';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { catchError, EMPTY, lastValueFrom, map } from 'rxjs';

import { WindowsWrapperService } from '@app/_core/services/windows-wrapper.service';
import { ResponseHandler } from '../helper/ResponseHandler';
import { UserStatus, Utils } from '../helper/Utils';
import { UserService } from './user.service';
import { WebStorageService } from './webstorage.service';
import { EncryDecryService } from '@app/_core/services/encrydecry.service';
import { AccountLockComponent } from '@app/pages/common/account-lock/account-lock.component';
import { AppService } from '@app/_core/services/app.service';

export interface loginModel {
  uname: string;
  pcode: string;
  company: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  errorCode: any;
  loginModel: loginModel;
  clientAbbreviation: any = '';
  companyCode: string = '';
  errorMessage: string = '';
  returnUrl: any;
  sessionID: any = 0;
  tempUserLoginData: any;
  
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private webStorageService: WebStorageService,
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private securityService: SecurityService,
    private configService: ConfigService,
    private EncryDecryService:EncryDecryService,
    private OnvidaWrapper: WindowsWrapperService,
    private appService: AppService
  ) {}

  setLoginData(loginModel: loginModel) {
    this.loginModel = loginModel;
  }

  getLoginData() {
    return this.loginModel;
  }

  handleLogginIn() {
    this.EncryDecryService.setKey(window.localStorage.getItem('_temp')!);
    const data = this.tempUserLoginData;
    const response = data?.Response?.Data?.Status;
    if (data.Response.Status === UserStatus.LOGGED_IN && response === undefined) {
      if (data.Response.ReloadRequired) {
        location.reload();
        return;
      } else {
        this.sessionID = data.Response.SessionID;
        this.clientAbbreviation = data.Response.ClientAbbreviation;
        this.companyCode = data.Response.ClientShortName;
        if(this.loginModel!=undefined){
          // SECURITY: Set encryption key derived from username for session data encryption
          // Key is stored in sessionStorage (not localStorage) and cleared on browser close
          // This is defense-in-depth; server-side tokens are primary security mechanism
          this.webStorageService.setKey(this.loginModel[`uname`]);
        }
        localStorage.setItem('UserJustLoggedIn', 'true');
        this.webStorageService.setItemInSessionStorage('currentUser', data.Response.User);
        this.webStorageService.setItemInSessionStorage('loginParams', data.Response.LoginParams);
        this.webStorageService.setItemInSessionStorage('company', data.Response.ClientShortName);
        this.webStorageService.setItemInSessionStorage('token', data.Response.Token);
        this.webStorageService.setItemInSessionStorage('clientAbbreviation', data.Response.ClientAbbreviation);
        this.webStorageService.setItemInSessionStorage('ClientAttrs', data.Response.ClientAttrs);
        this.webStorageService.setItemInSessionStorage('SessionID', data.Response.SessionID);
        this.webStorageService.setItemInSessionStorage('ASPNET_SessionId', data.Response.ASPNET_SessionId);
        this.webStorageService.setItemInSessionStorage('AdminUrl', data.Response.AdminUrl);
        this.webStorageService.setItemInSessionStorage('DashboardUrl', data.Response.DashboardUrl);
        this.webStorageService.setItemInSessionStorage('AccessibleClients', data.Response.AccessibleClients);
        this.userService.setCurrentUser(data.Response.User);
        Utils.store('IsAIAudits', data.Response.LoginParams.IsAIAuditsEnabled);
        Utils.store('DefaultTwilioErrorOccurance', data.Response.LoginParams.DefaultTwilioErrorOccurance);
        Utils.store('clientAbbreviation', this.clientAbbreviation);
        this.securityService.setHubManagerCalledStatus(false);
        this.securityService.setUserData(data.Response);
      }

      if(this.OnvidaWrapper.IsWrapper() && window?.location?.origin)
      {
        let webApiUrl = window.location.origin + '/webapi/api/';
        this.OnvidaWrapper.WebApiUrl(webApiUrl);
      }

      //let url = this.configService.geturl(this.clientAbbreviation);
      let url = data?.Response?.LoginParams?.ExternalAPIURL;
      if(url){
        if(this.OnvidaWrapper?.IsWrapper() && (this.clientAbbreviation == 'BCH' || this.clientAbbreviation == 'PCH' ||this.clientAbbreviation == 'AHP')){
          url = 'cr' + url;
          this.OnvidaWrapper.EnableCRProtocol(true);
        }
        if (!window.location.search.includes('audio') || !window.location.search.includes('file')) {
          this.configService.OpenUrlInNewTab(url);
       }
      }
      
      this.webStorageService.setItemInLocalStorage('data', data?.Response);
      this.webStorageService.setItemInLocalStorage('user', data?.Response?.User);
      
      this.webStorageService.setItemInLocalStorage('ASPNET_SessionId', data.Response.ASPNET_SessionId);
      this.webStorageService.setItemInLocalStorage('company', data.Response.ClientShortName);
      this.webStorageService.setItemInLocalStorage('token', data.Response.Token);
      this.webStorageService.setItemInLocalStorage('ClientAttrs', data.Response.ClientAttrs);
      this.webStorageService.setItemInLocalStorage('SessionID', data.Response.SessionID);
      this.errorMessage = '';
      if(window.location.search.includes('audio'))
      {
        let urlData=this.route.snapshot.queryParams['filePath']
        this.router.navigate([this.clientAbbreviation + '/audio'],{queryParams:{'filePath':urlData}})
      }
      else if(window.location.search.includes('file'))
        {
          let urlData=this.route.snapshot.queryParams['filePath']
          this.router.navigate([this.clientAbbreviation + '/file'],{queryParams:{'filePath':urlData}})
        }
      else
      {
        this.router.navigate([this.clientAbbreviation + '/Queue']);
      }
    } else {
      const response = data?.Response?.Data?.Status;
      if (response !== undefined) {
        this.loginFailed(data.Response.Data.Status);
      } else {
        this.loginFailed(data.Response.Status);
      }
    }
  }

  
  getLoggedIn() {
    const pcode = CryptoJS.SHA1(this.loginModel[`pcode`]).toString();
    this.userService
      .login(this.loginModel[`uname`], pcode, this.loginModel[`company`], this.loginModel[`SessionID`])
      .pipe(
        map((data: any) => {
          const isValid = ResponseHandler.processResponse(data, 'login');
          if (isValid) {
            this.tempUserLoginData = data;
            if (data?.Response?.LoginParams != null) {
              if (!data?.Response?.LoginParams?.DuplicateSession) {
                this.handleLogginIn();
              } else {
                this.duplicateUser();
              }
            } else {
              if(data.Response.Status===7 && data?.Response?.Message){
                this.navigateToExternalPage(data?.Response?.Message);
              }
              this.loginFailed(data.Response.Status);
            }
          }
        }),
        catchError(error => {
         this.CheckreturnUrl();
          return EMPTY;
          
        })
      )
      .subscribe(); 
    this.errorMessage = '';
    return this.errorMessage;
  }

  getSSOCallbackLoggedIn(token,client) {
    this.userService.SSOCallbackLoggedIn(token,client)
      .pipe(
        map((data: any) => {
          const isValid = ResponseHandler.processResponse(data, 'login');
          if (isValid) {
            this.tempUserLoginData = data;
            if (data?.Response?.LoginParams != null) {
              if (!data?.Response?.LoginParams?.DuplicateSession) {
                this.handleLogginIn();
              } else {
                this.duplicateUser();
              }
            } else {
              this.loginFailed(data.Response.Status);
            }
          }else {
            //this.loginFailed(data.Response.Status);
            this.router.navigate([''],{queryParams:{'Status':"Invalid Login"}})
          }
        }),
        catchError(error => {
          const userRole = 'admin';
          if (userRole.toLowerCase() === 'admin') {
            this.router.navigate([this.clientAbbreviation + '/Queue']);
          } else {
            this.router.navigate([this.clientAbbreviation + this.returnUrl]);
          }
          return EMPTY;
        })
      ).subscribe();
    this.errorMessage = '';
    return this.errorMessage;
  }

  async switchClientLoggedIn(sourceValue,voiceCountInterval,userinterval) {
    if (voiceCountInterval) {
      clearInterval(voiceCountInterval);
    }

    if ( userinterval) {
      clearInterval( userinterval);
    }
    
    const data: any = await lastValueFrom(this.userService.ChangeClientContext(sourceValue));

    
    // this.userService.ChangeClientContext(sourceValue).pipe(
    //   map((data: any)  => {
          const isValid = ResponseHandler.processResponse(data, 'ChangeClientContext');
          if (isValid) {
            this.tempUserLoginData = data;
            if (data?.Response?.LoginParams != null) {
              if (!data?.Response?.LoginParams?.DuplicateSession) {
                  this.handleLogginIn();
              } else {
                this.duplicateUser();
              }
            } else {
              this.loginFailed(data.Response.Status);
            }
          }
        // }),
        else{

        
        // catchError(error => {
          const userRole = 'admin';
          if (userRole.toLowerCase() === 'admin') {
            this.router.navigate([this.clientAbbreviation + '/Queue']);
          } else {
            this.router.navigate([this.clientAbbreviation + this.returnUrl]);
          }
          return EMPTY;
        // })
      }
      // )
      // .subscribe();
     await this.router.navigate([this.clientAbbreviation + '/Queue']);
    setTimeout(async () => {
      await  window.location.reload();
      }, 300);
      
    this.errorMessage = '';
    return this.errorMessage;
  }


  handleSecondLogginIn() {
    const data = this.tempUserLoginData;
    this.userService
      .cancelOtherSession(data)
      .pipe(
        map((data: any) => {
          this.handleLogginIn();
        }),
        catchError(error => {
          console.log('error', error);
          // this.router.navigate([this.clientAbbreviation + '/Queue']);
          return EMPTY;
        })
      )
      .subscribe();
  }

  duplicateUser() {
    const config: ModalOptions = { class: 'modal-sm' };

    this.bsModalRef = this.modalService.show(DuplicateUserComponent, config);
    // this.bsModalRef?.content?.event?.subscribe((result: any) => {
      // console.log('result', result);
    // });
  }

  ShowInActiveSession() {
    const config: ModalOptions = { class: 'modal-sm' };
    this.bsModalRef = this.modalService.show(ErrorPageComponent, config);
    this.bsModalRef.content.event.subscribe((result: any) => {});
  }

  loginFailed(error) {
    switch (error) {
      case UserStatus.INVALID_LOGIN:
        this.errorMessage = 'Invalid Login.';
        break;
      case UserStatus.INVALID_COMPANY:
        this.errorMessage = 'Invalid company for user.';
        break;
      case UserStatus.INVALID_PASSWORD:
        this.errorMessage = 'Invalid Password.';
        break;
      case UserStatus.ACCESS_DENIED:
        this.errorMessage = 'Access Denied.';
        break;
      case UserStatus.LOCKED_OUT:
        this.errorMessage = 'Account Locked.';
        this.accountLocked();
        break;
    }
    return this.errorMessage;
  }

  accountLocked(){
    this.userService.AccountLocked(this.loginModel[`company`], this.loginModel[`uname`]).pipe(
      map((data: any) => {
        const config: ModalOptions ={
          class: 'modal-sm modal-dialog-centered'
        }
        this.bsModalRef = this.modalService.show(AccountLockComponent, config);
      }),
      catchError(error => {
        console.log('error', error);
        return EMPTY;
      })
    ).subscribe();
  }
  getErrorMessage(errorCode: any) {
    if (errorCode) return this.loginFailed(errorCode);
    else return this.errorMessage;
  }

  getErrorCode() {
    return this.errorCode;
  }

  setErrorCode(errorCode: any) {
    this.errorCode = errorCode;
  } 
  SwitchPod(creds: any) { 
    this.userService
      .SwitchPod(creds)
      .pipe(
        map((data: any) => {
          const isValid = ResponseHandler.processResponse(data, 'SwitchPod');
          if (isValid) {
            this.tempUserLoginData = data;
            if (data?.Response?.LoginParams != null) {
              if (!data?.Response?.LoginParams?.DuplicateSession) {
                this.handleLogginIn();
              } else {
                this.duplicateUser();
              }
            } else {
              this.loginFailed(data.Response.Status);
            }
          }
        }),
        catchError(error => {
          this.CheckreturnUrl();
          return EMPTY;          
        })
      )
      .subscribe(); 
    this.errorMessage = '';
    return this.errorMessage;
  }
  CheckreturnUrl(){
    const userRole = 'admin';
    if(this.clientAbbreviation) {
    if (userRole.toLowerCase() === 'admin') {
      this.router.navigate([this.clientAbbreviation + '/Queue']);
    } else {
      this.router.navigate([this.clientAbbreviation + this.returnUrl]);
    }
  }
  }
  navigateToExternalPage(redirectionUrl): void {
    if(redirectionUrl.includes('https://') || redirectionUrl.includes('http://'))
      window.location.assign(redirectionUrl);
  }
}
